/**
 * B2BMarketplacePage Component
 * Main B2B marketplace page with:
 * - 4-column responsive product grid
 * - Top filter bar with dropdowns
 * - Inquiry modal popup
 * - Products fetched from real database via commerce service API
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { cn } from '@/lib/utils';
import { B2BProductCard } from '../components/B2BProductCard';
import { B2BFilterBar } from '../components/B2BFilterBar';
import { B2BInquiryModal } from '../components/B2BInquiryModal';
import {
  fetchFeaturedProducts,
  submitB2BInquiry,
  MarketplacePublicApiError,
} from '../services/marketplacePublicService';

import type {
  B2BProduct,
  B2BFilterOptions,
  B2BInquiryFormData,
  B2BMarketplacePageProps,
  ProductGrade,
  StockStatus,
} from '../types/b2bMarketplace.types';
import type { ProductCategory } from '../types/products.types';
import { ShoppingBag, Truck, ShieldCheck, Clock, Loader2, RefreshCw, AlertCircle, Search, ChevronDown } from 'lucide-react';

/**
 * Page Constants
 */
const PAGE_TITLE = 'SAUBHAGYA B2B Marketplace - Biogas Products & Fertilizers';
const PAGE_DESCRIPTION =
  'Discover quality biogas products, havan logs, funeral logs, and organic fertilizers from verified sellers. Get custom quotes for bulk orders.';

/**
 * Category Options for Filter
 */
const CATEGORY_OPTIONS: { value: ProductCategory; label: string }[] = [
  { value: 'BIOMASS', label: 'Biomass' },
  { value: 'FERTILIZER', label: 'Fertilizers' },
  { value: 'BIOGAS', label: 'Biogas' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'SEEDS', label: 'Seeds' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Grade Options for Filter
 */
const GRADE_OPTIONS: { value: ProductGrade; label: string }[] = [
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'GRADE_A', label: 'Grade A' },
  { value: 'GRADE_B', label: 'Grade B' },
];

/**
 * Location Options for Filter
 */
const LOCATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'Delhi', label: 'Delhi' },
  { value: 'Mumbai', label: 'Mumbai' },
  { value: 'Bangalore', label: 'Bangalore' },
  { value: 'Hyderabad', label: 'Hyderabad' },
  { value: 'Chennai', label: 'Chennai' },
  { value: 'Kolkata', label: 'Kolkata' },
  { value: 'Pune', label: 'Pune' },
  { value: 'Ahmedabad', label: 'Ahmedabad' },
  { value: 'Jaipur', label: 'Jaipur' },
  { value: 'Lucknow', label: 'Lucknow' },
];

/**
 * Trust Badges Data
 */
const TRUST_BADGES = [
  {
    icon: ShoppingBag,
    label: 'Verified Products',
    description: 'Quality assured',
  },
  {
    icon: Truck,
    label: 'Pan-India Delivery',
    description: 'Fast shipping',
  },
  {
    icon: ShieldCheck,
    label: 'Secure Transactions',
    description: 'Safe payments',
  },
  {
    icon: Clock,
    label: 'Quick Response',
    description: 'Within 4 hours',
  },
];

/**
 * Header colors for product cards based on category
 */
const CATEGORY_HEADER_COLORS: Record<string, string> = {
  BIOGAS: 'green',
  BIOMASS: 'amber',
  FERTILIZER: 'purple',
  EQUIPMENT: 'blue',
  SEEDS: 'teal',
  GAUSHALA_PRODUCTS: 'orange',
  OTHER: 'gray',
};

/**
 * Map API response to B2BProduct format
 * Converts PublicProductResponse from API to B2BProduct with sensible defaults
 */
interface ApiProduct {
  id?: string | number;
  externalId?: string;
  productName: string;
  description: string;
  category: ProductCategory;
  price: number;
  unit: string;
  primaryImage?: string;
  images?: string[] | { url: string }[]; // Backend stores images as array
  createdAt: string;
  quantityAvailable?: number;
}

function mapApiProductToB2B(apiProduct: ApiProduct, index: number): B2BProduct {
  // Get the product ID - support both 'id' and 'externalId' fields
  const productId = String(apiProduct.externalId || apiProduct.id || `product-${index}`);

  // Ensure product name is not empty
  const productName = (apiProduct.productName || apiProduct.name || '').trim() || `Product ${index + 1}`;

  const category = apiProduct.category || 'OTHER';
  const headerColor = CATEGORY_HEADER_COLORS[category] || 'green';

  // Determine stock status based on quantity
  let stockStatus: StockStatus = 'IN_STOCK';
  if (apiProduct.quantityAvailable !== undefined) {
    if (apiProduct.quantityAvailable === 0) {
      stockStatus = 'OUT_OF_STOCK';
    } else if (apiProduct.quantityAvailable < 10) {
      stockStatus = 'LIMITED_STOCK';
    }
  }

  return {
    id: productId,
    name: productName,
    description: apiProduct.description || 'Quality product from verified seller',
    sku: `SKU-${productId.slice(0, 8).toUpperCase()}`,
    category: category,
    subcategory: undefined,

    // Pricing - use actual price from API
    priceMin: Number(apiProduct.price) || 0,
    priceMax: apiProduct.price ? Number(apiProduct.price) * 1.2 : 0, // 20% range for bulk pricing
    unit: apiProduct.unit || 'unit',
    priceOnRequest: !apiProduct.price,

    // MOQ & Lead Time - sensible defaults
    moq: 1,
    moqUnit: apiProduct.unit || 'unit',
    leadTimeDays: {
      min: 3,
      max: 7,
    },

    // Stock & Grade
    stockStatus: stockStatus,
    grade: 'GRADE_A' as ProductGrade,

    // Origin & Certifications - defaults
    processingUnit: {
      id: 'default-unit',
      name: 'SAUBHAGYA Processing Unit',
      location: 'India',
    },
    certifications: [],

    // Ratings - defaults
    rating: 4.0 + Math.random() * 1, // Random between 4.0-5.0
    reviewCount: Math.floor(Math.random() * 50) + 5, // Random reviews

    // Images - handle both array of images and single primaryImage from backend
    images: (() => {
      const imageArray: any[] = [];

      // Priority 1: Use images array from backend (new format)
      if (apiProduct.images && Array.isArray(apiProduct.images)) {
        apiProduct.images.forEach((imageUrl: string | any, idx: number) => {
          const url = typeof imageUrl === 'string' ? imageUrl : imageUrl.url;
          if (url) {
            // Backend now returns full remote file service URLs - use as-is
            imageArray.push({
              id: `${idx}`,
              url: url,
              alt: productName,
              isPrimary: idx === 0,
            });
          }
        });
      }

      // Priority 2: Fall back to primaryImage field (legacy format)
      // Backend now returns full remote file service URLs - use as-is
      if (imageArray.length === 0 && apiProduct.primaryImage) {
        imageArray.push({
          id: '1',
          url: apiProduct.primaryImage,
          alt: productName,
          isPrimary: true
        });
      }

      return imageArray;
    })(),
    headerColor: headerColor,
    icon: undefined,

    // Metadata
    status: 'ACTIVE',
    createdAt: apiProduct.createdAt,
    updatedAt: apiProduct.createdAt,
  };
}

/**
 * B2BMarketplacePage Component
 */
export const B2BMarketplacePage: React.FC<B2BMarketplacePageProps> = ({ className }) => {
  // Products State - fetched from API
  const [products, setProducts] = useState<B2BProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter State
  const [filters, setFilters] = useState<B2BFilterOptions>({
    category: null,
    grade: null,
    certification: null,
    processingUnit: null,
    stockStatus: null,
    location: null,
    search: '',
  });

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<B2BProduct | null>(null);

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[B2BMarketplacePage] Fetching products from API...');

      const response = await fetchFeaturedProducts({
        page: 0,
        size: 100, // Fetch up to 100 products
        sort: 'createdAt,desc',
      });

      console.log('[B2BMarketplacePage] API Response:', response);

      // Handle Spring Page response format
      let apiProducts: ApiProduct[] = [];

      if (response.data && Array.isArray(response.data)) {
        // Format: { data: [...], pagination: {...} }
        apiProducts = response.data;
      } else if (Array.isArray(response)) {
        // Format: direct array
        apiProducts = response;
      } else if ((response as any).content && Array.isArray((response as any).content)) {
        // Format: Spring Page { content: [...], pageable: {...}, totalElements: ... }
        apiProducts = (response as any).content;
      }

      // Map API products to B2B format
      const b2bProducts = apiProducts.map((p, idx) => mapApiProductToB2B(p, idx));

      console.log('[B2BMarketplacePage] Mapped products:', b2bProducts.length);
      setProducts(b2bProducts);
    } catch (err) {
      console.error('[B2BMarketplacePage] Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Get dropdown options
  const processingUnits = useMemo(() => [], []);
  const certifications = useMemo(() => [], []);

  // Filter products (client-side filtering)
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Category filter
    if (filters.category) {
      result = result.filter(p => p.category === filters.category);
    }

    // Grade filter
    if (filters.grade) {
      result = result.filter(p => p.grade === filters.grade);
    }

    // Location filter
    if (filters.location) {
      result = result.filter(p =>
        p.processingUnit.location.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [products, filters]);

  // Handlers
  const handleFiltersChange = useCallback((newFilters: Partial<B2BFilterOptions>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleGetQuote = useCallback((product: B2BProduct) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleSubmitInquiry = useCallback(async (data: B2BInquiryFormData): Promise<string | undefined> => {
    if (!selectedProduct) {
      throw new Error('No product selected');
    }

    console.log('[B2BMarketplacePage] Submitting inquiry:', {
      product: selectedProduct.name,
      ...data,
    });

    try {
      // Call the real API with properly formatted data
      const response = await submitB2BInquiry({
        productExternalId: selectedProduct.id, // This is the external ID
        productName: selectedProduct.name,
        customerName: data.fullName,
        customerEmail: data.email,
        customerPhone: data.phone,
        quantity: data.quantity,
        quantityUnit: selectedProduct.moqUnit,
        location: data.location,
        additionalRequirements: data.additionalRequirements,
      });

      console.log('[B2BMarketplacePage] Inquiry submitted successfully:', response);

      return response.inquiryId;
    } catch (error) {
      console.error('[B2BMarketplacePage] Error submitting inquiry:', error);

      // Re-throw with user-friendly message
      if (error instanceof MarketplacePublicApiError) {
        if (error.statusCode === 429) {
          throw new Error('Too many inquiries. Please try again in 1 hour.');
        }
        if (error.statusCode === 409) {
          throw new Error('You have already submitted an inquiry for this product.');
        }
        if (error.statusCode === 404) {
          throw new Error('Product not found. Please refresh the page.');
        }
        throw new Error(error.message || 'Failed to submit inquiry. Please try again.');
      }

      throw error;
    }
  }, [selectedProduct]);

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
      </Helmet>

      <div className={cn('min-h-screen bg-gray-50', className)}>
        {/* Header - Responsive */}
        <header className="bg-[#1B6B3C] h-16 flex items-center justify-between px-5 md:px-10 lg:px-20 sticky top-0 z-50">
          <div className="flex items-center gap-2">
            <span className="text-lg md:text-xl">🌿</span>
            <span className="text-white text-base md:text-lg font-semibold tracking-wide">SAUBHAGYA</span>
          </div>
          <button className="bg-[#FF8C00] hover:bg-[#E67E00] text-white px-4 md:px-6 py-2 rounded-md font-medium transition-all text-sm md:text-base">
            Contact Us
          </button>
        </header>

        <main role="main" id="main-content">
          {/* Hero Section - Updated to match MarketplaceLanding */}
          <section className="bg-[#1B6B3C] px-5 md:px-10 lg:px-20 py-10 md:py-14 lg:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-white text-2xl md:text-4xl lg:text-[44px] font-bold leading-tight mb-3 md:mb-4">
                SAUBHAGYA B2B Marketplace
              </h1>
              <p className="text-gray-200 text-sm md:text-base lg:text-[17px] leading-relaxed max-w-2xl mx-auto px-4 md:px-0">
                Quality biogas products, sacred logs, and organic fertilizers from verified
                sellers across India
              </p>
            </div>

            {/* Stats Cards - Responsive Grid (formerly Trust Badges) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mt-8 md:mt-10 lg:mt-12 max-w-5xl mx-auto">
              {TRUST_BADGES.map((badge, index) => {
                const Icon = badge.icon;
                return (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-5 lg:p-6 text-center">
                    <div className="flex flex-col items-center">
                      <Icon className="h-6 w-6 mb-2 text-white" />
                      <div className="text-white text-sm md:text-base font-medium">{badge.label}</div>
                      <div className="text-gray-200 text-xs md:text-sm">{badge.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Search & Filter Section - Responsive */}
          <section className="bg-white px-5 md:px-10 lg:px-20 py-4 md:py-5 lg:py-6 shadow-sm">
            <B2BFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              categories={CATEGORY_OPTIONS}
              grades={GRADE_OPTIONS}
              certifications={certifications}
              processingUnits={processingUnits}
              locations={LOCATION_OPTIONS}
            />
          </section>

          {/* Product Grid Section - Responsive */}
          <section className="px-5 md:px-10 lg:px-20 py-8 md:py-10 lg:py-12">
            <div className="max-w-7xl mx-auto">

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-600">
              {isLoading ? (
                'Loading products...'
              ) : (
                <>
                  Showing{' '}
                  <span className="font-semibold text-gray-900">
                    {filteredProducts.length}
                  </span>{' '}
                  products
                </>
              )}
            </p>
            {!isLoading && (
              <button
                onClick={fetchProducts}
                className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                aria-label="Refresh products"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading products from marketplace...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="w-12 h-12 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Failed to load products
              </h3>
              <p className="text-gray-600 max-w-md mb-6">
                {error}
              </p>
              <button
                onClick={fetchProducts}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          )}

              {/* Product Grid - 4 Columns on Desktop */}
              {!isLoading && !error && filteredProducts.length > 0 && (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 lg:gap-6"
                  role="list"
                  aria-label="Product listings"
                >
                  {filteredProducts.map((product) => (
                    <B2BProductCard
                      key={product.id}
                      product={product}
                      onGetQuote={handleGetQuote}
                    />
                  ))}
                </div>
              )}

          {/* Empty State */}
          {!isLoading && !error && filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <ShoppingBag className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No products found
              </h3>
              <p className="text-gray-600 max-w-md mb-6">
                {products.length === 0
                  ? 'No products are currently available in the marketplace. Check back soon!'
                  : "We couldn't find any products matching your filters. Try adjusting your search criteria."}
              </p>
              {products.length > 0 && (
                <button
                  onClick={() =>
                    setFilters({
                      category: null,
                      grade: null,
                      certification: null,
                      processingUnit: null,
                      stockStatus: null,
                      location: null,
                      search: '',
                    })
                  }
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
            </div>
          </section>
        </main>

        {/* Footer - Responsive */}
        <footer className="bg-[#1A2332] px-5 md:px-10 lg:px-20 py-8 md:py-10 lg:py-12">
          <div className="max-w-6xl mx-auto">
            {/* Logo & Description */}
            <div className="text-center mb-6 md:mb-8">
              <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
                <span className="text-xl md:text-2xl">🌿</span>
                <span className="text-white text-lg md:text-xl font-semibold">SAUBHAGYA B2B Marketplace</span>
              </div>
              <p className="text-gray-400 text-xs md:text-sm max-w-2xl mx-auto px-4 md:px-0">
                Your trusted platform for premium biogas and farming quality agricultural solutions
              </p>
            </div>

            {/* Footer Links */}
            <div className="flex items-center justify-center gap-4 md:gap-6 mb-6 md:mb-8 flex-wrap">
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">About Us</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Contact</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Terms & Conditions</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">FAQ</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Return Policy</a>
            </div>

            {/* Copyright */}
            <div className="text-center text-gray-500 text-[10px] md:text-xs pt-4 md:pt-6 border-t border-gray-700">
              Powered by Dhanush Healthcare Systems
            </div>
          </div>
        </footer>

        {/* Inquiry Modal */}
        <B2BInquiryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          product={selectedProduct}
          onSubmit={handleSubmitInquiry}
        />
      </div>
    </>
  );
};

export default B2BMarketplacePage;
